import { useRef, useState } from "react";
import { validate } from "./Validate";
import { ErrorState } from "../Types";
import * as yup from "yup";
import { validationFocus } from "../labels/Focus";

export enum ValidationHint {
  NoValidation,
  ClearValidation,
}

export interface SetState<S extends {}> {
  (arg: Partial<S>, hint?: (ValidationHint | (keyof S)[])): void;
}

export interface Validate {
  (container: string): Promise<boolean>;
}

export function useValidatedState<S extends {}>(
  initialState: S | (() => S),
  schema: yup.ObjectSchema<S>
): [S, SetState<S>, ErrorState<S>, Validate] {
  const [value, setValue] = useState<S>(initialState);
  return [value, ...useValidation<S>(schema, value, setValue)];
}

export function useValidation<S extends {}>(
  schema: yup.ObjectSchema<S>,
  value: S,
  onChange: (v: S) => any
): [SetState<S>, ErrorState<S>, Validate] {
  const [errors, setErrors] = useState<ErrorState<S>>({});
  const valueRef = useRef<S | null>(null);

  const validateInternal = async (v: S, errors: ErrorState<S>, paths?: string[]) => {
    valueRef.current = v;
    const [newErrors, original] = await validate(schema, v, paths);
    if (valueRef.current != original) {
      throw { cancelled: true };
    }
    if (!paths) {
      setErrors(newErrors);
    } else {
      const allErrors = { ...errors, ...newErrors };
      for (let p of paths) {
        if (p in newErrors) {
          continue;
        }
        delete allErrors[p as keyof S];
      }
      setErrors(allErrors);
    }
    return Object.keys(newErrors).length == 0;
  };

  const setValueState: SetState<S> = (v, hint?) => {
    let newValue = { ...value, ...v };

    onChange(newValue);

    if (hint == ValidationHint.ClearValidation) {
      setErrors({});
    }
    else if (hint == ValidationHint.NoValidation) {
    }
    else {
      let paths: string[];
      if (typeof hint == "object") {
        paths = hint.map(r => r.toString()).filter(r => r ? true : false);
      }
      else {
        paths = [];
        for (let p in v) {
          paths.push(p);
        }
      }
      validateInternal(newValue, errors, paths);
    }
  };

  const revalidate: Validate = async (container) => {
    let res = await validateInternal(value, errors);
    if (!res) {
      setTimeout(() => validationFocus(container), 1);
    }
    return res;
  };

  return [setValueState, errors, revalidate];
}
